import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './WelcomeSection.css';

// Array of background images to cycle through
const BACKGROUND_IMAGES = [
  '/images/backgrounds/csilla.jpg',
  '/images/backgrounds/ria.jpg',
  '/images/backgrounds/timiv.jpg',
  '/images/backgrounds/timi.jpg',
  '/images/backgrounds/lenka.jpg',
];

const WelcomeSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showIntroOverlay, setShowIntroOverlay] = useState(true);
  const [fadeOutIntro, setFadeOutIntro] = useState(false);
  
  const currentLanguage = i18n.language;
  const isSlovak = currentLanguage === 'sk';
  
  // Add UTF-8 meta tag to ensure proper encoding
  useEffect(() => {
    // Check if the charset meta tag already exists
    let charsetMeta = document.querySelector('meta[charset]');
    if (!charsetMeta) {
      // Create and append the charset meta tag if it doesn't exist
      charsetMeta = document.createElement('meta');
      charsetMeta.setAttribute('charset', 'utf-8');
      document.head.insertBefore(charsetMeta, document.head.firstChild);
    } else {
      // Ensure it's set to UTF-8
      charsetMeta.setAttribute('charset', 'utf-8');
    }
    
    // Also ensure Content-Type meta is present
    let contentTypeMeta = document.querySelector('meta[http-equiv="Content-Type"]');
    if (!contentTypeMeta) {
      contentTypeMeta = document.createElement('meta');
      contentTypeMeta.setAttribute('http-equiv', 'Content-Type');
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
      document.head.appendChild(contentTypeMeta);
    } else {
      contentTypeMeta.setAttribute('content', 'text/html; charset=UTF-8');
    }

    // Add Quicksand font from Google Fonts if not already present
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Quicksand"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(fontLink);
    }
  }, []);

  // Handle intro animation timing
  useEffect(() => {
    // After 2 seconds, start fading out
    const fadeStartTimeout = setTimeout(() => {
      setFadeOutIntro(true);
    }, 2000);

    // After 4 seconds total (2s display + 2s fade), remove the overlay
    const removeOverlayTimeout = setTimeout(() => {
      setShowIntroOverlay(false);
    }, 4000);

    // Clean up timeouts on unmount
    return () => {
      clearTimeout(fadeStartTimeout);
      clearTimeout(removeOverlayTimeout);
    };
  }, []);

  // Background image carousel effect
  useEffect(() => {
    // Set up the interval to change the background image every 10 seconds
    const intervalId = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % BACKGROUND_IMAGES.length);
    }, 10000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);
  
  // Preload and cache all background images
  useEffect(() => {
    const cacheBackgroundImages = async () => {
      try {
        // Cache key for the background images in localStorage 
        const CACHE_KEY = 'yoga_welcome_background_cache';
        
        // Check if we have a valid cache entry with timestamp
        const cachedData = localStorage.getItem(CACHE_KEY);
        
        if (cachedData) {
          const { timestamp, imageUrls } = JSON.parse(cachedData);
          const currentTime = new Date().getTime();
          const twelveHoursMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
          
          // If the cache is less than 12 hours old, use it
          if (currentTime - timestamp < twelveHoursMs) {
            return;
          }
        }
        
        // If we don't have a valid cache, preload all images
        const preloadPromises = [...BACKGROUND_IMAGES, '/images/backgrounds/2.jpg'].map(imageUrl => {
          return new Promise((resolve) => {
            const imageToCache = new Image();
            imageToCache.src = imageUrl;
            imageToCache.onload = () => resolve();
            
            // Also use link preload for better browser caching
            const link = document.createElement('link');
            link.rel = 'preload';
            link.href = imageUrl;
            link.as = 'image';
            document.head.appendChild(link);
          });
        });
        
        // Wait for all images to be preloaded
        await Promise.all(preloadPromises);
        
        // Store in localStorage with current timestamp
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          timestamp: new Date().getTime(),
          imageUrls: [...BACKGROUND_IMAGES, '/images/backgrounds/2.jpg']
        }));
        
      } catch (error) {
        // Silent fail for image caching
      }
    };
    
    cacheBackgroundImages();
  }, []);
  
  // Add meta tags for SEO
  useEffect(() => {
    // Set page title
    document.title = t('studio_name');
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('meta_description'));
    }
    
    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', t('studio_name'));
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', t('meta_description'));
    }
    
    // Load Quicksand font
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(fontLink);
    }
    
    // Load Cormorant Garamond font
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700"]')) {
      const garamondLink = document.createElement('link');
      garamondLink.rel = 'stylesheet';
      garamondLink.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(garamondLink);
    }
    
  }, [t]);
  
  const handleScheduleClick = () => {
    // Using the same approach as in Header.js
    // First check if we're on the home page
    if (window.location.pathname === '/' || window.location.pathname === '/sk/' || window.location.pathname === '/en/') {
      // Try to find the schedule section by class name
      const scheduleSection = document.querySelector('.schedule-section');
      if (scheduleSection) {
        scheduleSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start' 
        });
        return;
      }
      
      // Also try by ID if it exists
      const scheduleSectionById = document.getElementById('schedule-section');
      if (scheduleSectionById) {
        scheduleSectionById.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start' 
        });
        return;
      }
    }
    
    // If we're not on the home page or couldn't find the section, navigate to home and then scroll
    navigate('/');
    
    // Wait for navigation to complete
    setTimeout(() => {
      const scheduleSection = document.querySelector('.schedule-section');
      if (scheduleSection) {
        scheduleSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start' 
        });
      }
    }, 100);
  };
  
  const handleClassesClick = () => {
    navigate('/classes');
  };
  
  return (
    <section 
      className="welcome-section"
      style={{
        backgroundImage: `url(${BACKGROUND_IMAGES[currentImageIndex]})`,
        transition: 'background-image 1s ease-in-out'
      }}
    >
      {showIntroOverlay && (
        <div 
          className={`intro-overlay ${fadeOutIntro ? 'fade-out' : ''}`}
          style={{
            backgroundImage: 'url(/images/backgrounds/2.jpg)'
          }}
        ></div>
      )}
      <div className="overlay"></div>
      <div className="welcome-content">
        <h1>{t('studio_name')}</h1>
        <div className="motto">{t('breathe_discover_lightness')}</div>        
        <div className="welcome-buttons">
          <button onClick={handleScheduleClick} className="btn primary-btn">{t('class_schedule')}</button>
          <button onClick={handleClassesClick} className="btn secondary-btn">{t('view_classes')}</button>
        </div>
      </div>
    </section>
  );
};

export default WelcomeSection; 